import { Component, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { LoaderCircle } from 'lucide-react';
import { Link, usePath } from './router';
import Home from './pages/Home';
import { ROUTES } from './routes';

function Centered({ children }: { children: ReactNode }) {
  return <div className="app-shell"><main className="tool-page centered">{children}</main></div>;
}

function NotFound() {
  return (
    <Centered>
      <h1>Page not found</h1>
      <p className="lede">That page does not exist.</p>
      <Link to="/" className="primary-button">Back to all tools</Link>
    </Centered>
  );
}

class Boundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Centered>
        <h1>Something went wrong</h1>
        <p className="lede">{this.state.error.message}</p>
        <a className="primary-button" href="/">Back to all tools</a>
      </Centered>
    );
  }
}

export default function Root() {
  const path = usePath();
  if (path === '/') return <Home />;
  const Page = ROUTES[path];
  if (!Page) return <NotFound />;
  return (
    // Keyed by path so a crashed tool recovers when you navigate elsewhere.
    <Boundary key={path}>
      <Suspense fallback={<Centered><LoaderCircle className="spin upload-icon" size={32} /></Centered>}>
        <Page />
      </Suspense>
    </Boundary>
  );
}
